import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { notificationsApi } from "../shared/services/api";
import { useAuth } from "./AuthContext";

const NotificationCenterContext = createContext(null);

export const NotificationCenterProvider = ({ children }) => {
  const { userLoggedIn, sessionReady } = useAuth();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!sessionReady || !userLoggedIn) {
      setItems([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    try {
      const result = await notificationsApi.list({ page: 1, pageSize: 8 });
      setItems(result.data || []);
      setUnreadCount(Number(result.meta?.unreadCount || 0));
    } catch (error) {
      setItems([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [sessionReady, userLoggedIn]);

  useEffect(() => {
    if (!sessionReady || !userLoggedIn) {
      setItems([]);
      setUnreadCount(0);
      return undefined;
    }

    refresh();
    const timer = window.setInterval(refresh, 30000);

    return () => window.clearInterval(timer);
  }, [refresh, sessionReady, userLoggedIn]);

  const markRead = useCallback(
    async (id) => {
      await notificationsApi.markRead(id);
      await refresh();
    },
    [refresh]
  );

  const markAllRead = useCallback(async () => {
    await notificationsApi.markAllRead();
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      items,
      unreadCount,
      loading,
      refresh,
      markRead,
      markAllRead
    }),
    [items, unreadCount, loading, refresh, markRead, markAllRead]
  );

  return <NotificationCenterContext.Provider value={value}>{children}</NotificationCenterContext.Provider>;
};

export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error("useNotificationCenter must be used within a NotificationCenterProvider");
  }
  return context;
};
